// Stav objednávky, /stav-objednavky/ (#461). Vlastní stránka místo doplňku dkLAB:
// formulář číslo + e-mail bez captchy, stav z API `order-status` na s1
// (search/api/order-status.mjs). Plátno docs/design/2026-10-07-stav-objednavky/
// (schválené 7. 10. 2026) je závazný prototyp, paritu měří
// tools/audit/order-status-parity.mjs, chování tools/devcheck/stav-objednavky.mjs.
// Na ostré CZ i SK od 9. 10. 2026, doplněk dkLAB je odinstalovaný.
//
// Tok:
//   · odkaz z e-mailu `#o=<kód>&e=<e-mail>` (nebo starý `?email=&order=` doplňku)
//     přečte už hlavička (common-header.html) a adresu očistí dřív, než ji cokoli
//     změří; stránka údaje dostane ve `window.__bsOrderPrefill` a hned hledá;
//   · e-mail přihlášeného zákazníka z dataLayeru předvyplní pole;
//   · úspěšné hledání si pamatuje localStorage `bs-os-last` 30 dní („Naposledy
//     jste hledali", odkaz Zapomenout);
//   · e-mail ověřené objednávky dostane chat (`setChatContact`) a okno chatu otevřené
//     klikem má v poli předvyplněné číslo a e-mail objednávky, jen dokud stránka
//     tu objednávku ukazuje: neúspěšné nebo jiné hledání obojí zahodí;
//   · číslo objednávky ani e-mail nikdy nejdou do URL (API je čte z těla POST);
//   · návody k položkám (`items[].files`, soubor indexeru, Tomáš 9. 10. 2026): karta
//     pod stavem a odkaz u položky, kdy co ukázat říká model (`manualsOf`, `itemsOf`).
// Data kreslí orderStatusModel.js (čistý model), sem patří jen DOM.

import { icon } from './icon.js';
import { logo, hasLogo, carrierLogoId } from './logo.js';
import { ensureLogos } from './injectSprite.js';
import { el, link, pageHost, pageIs, markReady, buildHero } from './infoPage.js';
import { CONTACT, isOpenNow, openWindow } from './headerTools.js';
import { nextPickupDay, pickupWhen } from './shippingModel.js';
import { asChatTrigger, chatFallbackHref, chatOn, setChatContact } from './supportChat.js';
import { showFeedback } from './feedbackToast.js';
import { getOrderStatusConfig } from './searchConfig.js';
import { lookupOrder, fetchDocument, cancelOrder } from './orderStatusApi.js';
import { featureOn } from './features.js';
import { page } from './pages.js';
import { shoptetData } from '../shoptet-hooks.js';
import SHOP from '../../config/shop.js';
import { plural } from '../locale/plural.js';
import { money } from './money.js';
import {
  cleanCode,
  cleanEmail,
  parsePrefill,
  cleanSearch,
  readMemory,
  writeMemory,
  forgetMemory,
  toDate,
  clock,
  updatedLabel,
  statusOf,
  stepsOf,
  actionOf,
  faqOf,
  helpOf,
  cancelOf,
  transferOf,
  trackingOf,
  estimateOf,
  estimateLabel,
  returnUntil,
  staleResult,
  itemsOf,
  manualsOf,
  documentsOf,
  shippingKind,
  fullDate,
  httpsUrl,
} from './orderStatusModel.js';
import T from '../locale/cs/orderStatusPage.js';

const CHAT_SOURCE = 'order-status';
const MAX_LOCK_MS = 3600 * 1000;

// Samotné ČTENÍ `window.localStorage` hází, když má prohlížeč úložiště
// zakázané (checkoutPage.js storageOrNull) → getter v try
function storageOrNull() {
  try {
    return window.localStorage || null;
  } catch (e) {
    return null;
  }
}

function loggedEmail() {
  const c = shoptetData('customer');
  return c && c.registered === true ? cleanEmail(c.email) : '';
}

function mailHref(code) {
  return 'mailto:' + CONTACT.email + (code ? '?subject=' + encodeURIComponent(T.help.subject(code)) : '');
}

function hourText(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return hh + ':' + (mm < 10 ? '0' : '') + mm;
}

function section(cls, titleId) {
  const s = el('section', cls);
  if (titleId) s.setAttribute('aria-labelledby', titleId);
  return s;
}

function h2(cls, text, id) {
  const h = el('h2', cls, text);
  if (id) h.id = id;
  return h;
}

function box(cls, name) {
  const span = el('span', cls);
  span.appendChild(icon(name));
  return span;
}

// Vstup do chatu (se zapnutým chatem), jinak e-mail s číslem objednávky v předmětu
function contactLink(cls, code, chatLabel, mailLabel) {
  const chat = chatOn();
  const a = link(chat ? chatFallbackHref() : mailHref(code), cls);
  a.appendChild(icon(chat ? 'chat' : 'mail'));
  a.appendChild(el('span', null, chat ? chatLabel : mailLabel));
  if (chat) asChatTrigger(a, CHAT_SOURCE);
  return a;
}

function copyButton(cls, value, what) {
  const b = el('button', cls);
  b.type = 'button';
  b.setAttribute('aria-label', T.pay.copy(what));
  b.appendChild(icon('copy'));
  b.addEventListener('click', function () {
    const done = function () {
      showFeedback(T.pay.copied);
    };
    const fail = function () {
      showFeedback(T.pay.copyFailed, 'error');
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(value).then(done, fail);
      else fail();
    } catch (e) {
      fail();
    }
  });
  return b;
}

function arrowLink(href, cls, text) {
  const a = link(href, cls);
  a.appendChild(el('span', null, text));
  a.appendChild(icon('arrow-right'));
  return a;
}

// ---------------------------------------------------------------------------
// Boční sloupec
// ---------------------------------------------------------------------------
function buildWhere() {
  const card = section('bs-ord__where', 'os-where');
  const head = el('div', 'bs-ord__where-head');
  head.appendChild(box('bs-ord__where-icon', 'info'));
  head.appendChild(h2('bs-ord__where-title', T.whereTitle, 'os-where'));
  card.appendChild(head);
  const list = el('ul', 'bs-ord__where-list');
  T.where.forEach(function (item) {
    const li = el('li', 'bs-ord__where-item');
    li.appendChild(icon(item.icon, 'bs-ord__where-bullet'));
    const text = el('span', 'bs-ord__where-text');
    const name = item.href ? link(item.href, 'bs-ord__where-name', item.title) : el('b', 'bs-ord__where-name', item.title);
    text.appendChild(name);
    text.appendChild(document.createTextNode(', ' + item.desc));
    li.appendChild(text);
    list.appendChild(li);
  });
  card.appendChild(list);
  const note = el('div', 'bs-ord__where-note', T.whereNote[0]);
  note.appendChild(el('b', null, T.whereNote[1]));
  note.appendChild(document.createTextNode(T.whereNote[2]));
  card.appendChild(note);
  return card;
}

function buildFaq(keys) {
  const card = section('bs-ord__faq', 'os-faq');
  card.appendChild(h2('bs-ord__faq-title', T.faqTitle, 'os-faq'));
  keys.forEach(function (key, i) {
    const item = el('div', 'bs-ord__faq-item');
    const q = el('button', 'bs-ord__faq-q');
    q.type = 'button';
    q.id = 'os-faq-q' + i;
    q.setAttribute('aria-controls', 'os-faq-a' + i);
    q.appendChild(el('span', null, T.faq[key].q));
    q.appendChild(icon('chevron-down', 'bs-ord__faq-chev'));
    const a = el('p', 'bs-ord__faq-a', T.faq[key].a);
    a.id = 'os-faq-a' + i;
    const set = function (open) {
      q.setAttribute('aria-expanded', open ? 'true' : 'false');
      a.hidden = !open;
    };
    set(i === 0);
    q.addEventListener('click', function () {
      set(q.getAttribute('aria-expanded') !== 'true');
    });
    item.appendChild(q);
    item.appendChild(a);
    card.appendChild(item);
  });
  const all = page('faq');
  if (all) card.appendChild(arrowLink(all, 'bs-ord__faq-all', T.faqAll));
  return card;
}

function buildHelp(kind, code) {
  const chat = chatOn();
  const def = T.help[kind] || T.help.form;
  const card = section('bs-ord__help', 'os-help');
  card.appendChild(box('bs-ord__help-icon', 'chat'));
  card.appendChild(h2('bs-ord__help-title', def.title, 'os-help'));
  card.appendChild(el('p', 'bs-ord__help-text', typeof def.text === 'function' ? def.text(chat) : def.text));
  card.appendChild(contactLink('bs-ord__help-cta', code, T.help.chat, T.help.mail));
  if (chat) {
    const mail = link(mailHref(code), 'bs-ord__help-mail');
    mail.appendChild(icon('mail'));
    mail.appendChild(el('span', null, CONTACT.email));
    card.appendChild(mail);
  }
  card.appendChild(el('div', 'bs-ord__help-hours', T.help.hours(CONTACT.hours)));
  return card;
}

// ---------------------------------------------------------------------------
// Formulář
// ---------------------------------------------------------------------------
function field(id, name, type, label, placeholder, iconName, value) {
  const wrap = el('div', 'bs-ord__field');
  const lab = el('label', 'bs-ord__label', label);
  lab.setAttribute('for', id);
  wrap.appendChild(lab);
  const box2 = el('div', 'bs-ord__input-wrap');
  box2.appendChild(icon(iconName, 'bs-ord__input-icon'));
  const input = el('input', 'bs-ord__input');
  input.id = id;
  input.name = name;
  input.type = type;
  input.placeholder = placeholder;
  input.required = true;
  input.value = value || '';
  if (name === 'code') {
    input.inputMode = 'numeric';
    input.autocomplete = 'off';
    input.maxLength = 32;
  } else {
    input.autocomplete = 'email';
    input.maxLength = 254;
  }
  box2.appendChild(input);
  wrap.appendChild(box2);
  return { wrap: wrap, input: input };
}

function buildAlert(kind, retryMin, code) {
  const chat = chatOn();
  const A = T.alert;
  const defs = {
    not_found: { cls: 'nf', icon: 'help', title: A.notFound.title, text: A.notFound.text, text2: A.notFound.text2(chat) },
    locked: { cls: 'locked', icon: 'lock', title: A.locked.title, text: A.locked.text(retryMin, chat) },
    outage: { cls: 'outage', icon: 'info', title: A.outage.title, text: A.outage.text(chat) },
  };
  const d = defs[kind] || defs.outage;
  const box2 = el('div', 'bs-ord__alert bs-ord__alert--' + d.cls);
  box2.setAttribute('role', 'alert');
  box2.appendChild(icon(d.icon, 'bs-ord__alert-icon'));
  const body = el('div', 'bs-ord__alert-body');
  body.appendChild(el('div', 'bs-ord__alert-title', d.title));
  body.appendChild(el('p', 'bs-ord__alert-text', d.text));
  if (d.text2) body.appendChild(el('p', 'bs-ord__alert-text', d.text2));
  body.appendChild(contactLink('bs-ord__alert-cta', code, A.chat, A.mail));
  box2.appendChild(body);
  return box2;
}

function buildMemory(app, mem) {
  const row = el('div', 'bs-ord__mem');
  row.appendChild(box('bs-ord__mem-icon', 'clock'));
  const text = el('div', 'bs-ord__mem-text');
  text.appendChild(el('div', 'bs-ord__mem-label', T.memory.label));
  text.appendChild(el('div', 'bs-ord__mem-code', T.orderNo(mem.code)));
  row.appendChild(text);
  const actions = el('div', 'bs-ord__mem-actions');
  const show = link('#', 'bs-ord__mem-show');
  show.setAttribute('role', 'button');
  show.appendChild(el('span', null, T.memory.show));
  show.appendChild(icon('arrow-right'));
  show.addEventListener('click', function (e) {
    e.preventDefault();
    app.lookup(mem.code, mem.email, 'memory');
  });
  actions.appendChild(show);
  const forget = el('button', 'bs-ord__mem-forget', T.memory.forget);
  forget.type = 'button';
  forget.addEventListener('click', function () {
    // rozběhnuté hledání po návratu paměť znovu neuloží (review 8. 10. 2026)
    app.memoryEpoch += 1;
    forgetMemory(app.store);
    row.remove();
  });
  actions.appendChild(forget);
  row.appendChild(actions);
  return row;
}

function buildForm(app, opts) {
  const card = section('bs-ord__form', 'os-form-title');
  const head = el('div', 'bs-ord__form-head');
  head.appendChild(box('bs-ord__form-icon', 'package'));
  const titles = el('div', 'bs-ord__form-titles');
  titles.appendChild(h2('bs-ord__form-title', T.form.title, 'os-form-title'));
  titles.appendChild(el('div', 'bs-ord__form-sub', T.form.sub));
  head.appendChild(titles);
  card.appendChild(head);

  const lockedMs = opts.alert === 'locked' ? Math.min(MAX_LOCK_MS, (opts.retryAfter || 60) * 1000) : 0;
  const lockedMin = Math.max(1, Math.ceil(lockedMs / 60000));
  if (opts.alert) card.appendChild(buildAlert(opts.alert, lockedMin, opts.code));
  const mem = opts.alert ? null : readMemory(app.store);
  if (mem) card.appendChild(buildMemory(app, mem));

  const form = el('form', 'bs-ord__fields-form');
  form.setAttribute('novalidate', '');
  const grid = el('div', 'bs-ord__fields');
  const code = field('os-code', 'code', 'text', T.form.code, T.form.codePlaceholder, 'tag', opts.code);
  const logged = app.logged && (!opts.email || opts.email === app.logged);
  const email = field('os-email', 'email', 'email', T.form.email, T.form.emailPlaceholder, 'mail', opts.email || app.logged);
  if (logged) {
    const note = el('div', 'bs-ord__from-account');
    note.appendChild(icon('user'));
    note.appendChild(el('span', null, T.form.fromAccount));
    email.wrap.appendChild(note);
  }
  grid.appendChild(code.wrap);
  grid.appendChild(email.wrap);
  form.appendChild(grid);

  const row = el('div', 'bs-ord__submit-row');
  const submit = el('button', 'bs-ord__submit');
  submit.type = 'submit';
  const label = el('span', null, opts.alert === 'outage' ? T.form.retry : T.form.submit);
  submit.appendChild(label);
  submit.appendChild(icon('arrow-right'));
  if (lockedMs) {
    submit.disabled = true;
    label.textContent = T.form.lockedFor(lockedMin);
    app.timer = setTimeout(function () {
      submit.disabled = false;
      label.textContent = T.form.submit;
    }, lockedMs);
  }
  row.appendChild(submit);
  const privacy = el('span', 'bs-ord__privacy');
  privacy.appendChild(icon('lock'));
  privacy.appendChild(el('span', null, T.form.privacy));
  row.appendChild(privacy);
  form.appendChild(row);

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (submit.disabled) return;
    const c = cleanCode(code.input.value);
    const m = cleanEmail(email.input.value);
    code.input.setCustomValidity(c ? '' : T.form.missingCode);
    email.input.setCustomValidity(m ? '' : T.form.missingEmail);
    if (!c || !m) {
      form.reportValidity();
      return;
    }
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    label.textContent = T.form.searching;
    app.lookup(c, m, 'form');
  });
  [code.input, email.input].forEach(function (input) {
    input.addEventListener('input', function () {
      input.setCustomValidity('');
    });
  });
  card.appendChild(form);
  return { card: card, code: code.input, email: email.input };
}

// ---------------------------------------------------------------------------
// Načítání a výsledek
// ---------------------------------------------------------------------------
function statusCard(cls, iconName, eyebrow, title, text, meta) {
  const card = section('bs-ord__status ' + cls, title ? 'os-status' : null);
  card.appendChild(box('bs-ord__status-icon', iconName));
  const body = el('div', 'bs-ord__status-body');
  body.appendChild(el('div', 'bs-ord__status-eyebrow', eyebrow));
  const h = h2('bs-ord__status-title', title, 'os-status');
  h.tabIndex = -1;
  body.appendChild(h);
  body.appendChild(el('p', 'bs-ord__status-text', text));
  card.appendChild(body);
  if (meta != null) card.appendChild(el('div', 'bs-ord__status-meta', meta));
  return card;
}

function buildLoading(code, source) {
  const wrap = el('div', 'bs-ord__loading');
  wrap.setAttribute('aria-busy', 'true');
  wrap.appendChild(
    statusCard('bs-ord__status--loading', 'refresh', T.orderNo(code), T.loading.title(code), source === 'memory' ? T.loading.fromMemory : T.loading.fromLink, null)
  );
  const skel = function (rows) {
    const card = el('div', 'bs-ord__card bs-ord__skel');
    card.setAttribute('aria-hidden', 'true');
    card.appendChild(el('span', 'bs-ord__skel-title'));
    rows.forEach(function (r) {
      card.appendChild(r);
    });
    return card;
  };
  const steps = el('div', 'bs-ord__skel-steps');
  for (let i = 0; i < 3; i += 1) {
    steps.appendChild(el('span', 'bs-ord__skel-dot'));
    if (i < 2) steps.appendChild(el('span', 'bs-ord__skel-line'));
  }
  wrap.appendChild(skel([steps, el('span', 'bs-ord__skel-text')]));
  const item = function () {
    const row = el('div', 'bs-ord__skel-item');
    row.appendChild(el('span', 'bs-ord__skel-thumb'));
    const lines = el('span', 'bs-ord__skel-lines');
    lines.appendChild(el('span', 'bs-ord__skel-l1'));
    lines.appendChild(el('span', 'bs-ord__skel-l2'));
    row.appendChild(lines);
    return row;
  };
  wrap.appendChild(skel([item(), item()]));
  return wrap;
}

function buildSteps(order, now) {
  const card = section('bs-ord__card bs-ord__steps-card', 'os-steps');
  card.appendChild(h2('bs-ord__h2 bs-ord__steps-title', T.stepsTitle, 'os-steps'));
  const steps = stepsOf(order, now);
  const list = el('ol', 'bs-ord__steps');
  list.style.setProperty('--bs-ord-steps', String(steps.length));
  steps.forEach(function (s, i) {
    const li = el('li', 'bs-ord__step is-' + s.state);
    if (s.state === 'cur') li.setAttribute('aria-current', 'step');
    const rail = el('div', 'bs-ord__step-rail');
    const dot = el('span', 'bs-ord__dot');
    if (s.state === 'todo') dot.appendChild(el('span', 'bs-ord__dot-pending'));
    else dot.appendChild(icon(s.state === 'done' ? 'check' : s.icon || 'check'));
    rail.appendChild(dot);
    const line = el('span', 'bs-ord__line');
    if (i === steps.length - 1) line.classList.add('is-last');
    rail.appendChild(line);
    li.appendChild(rail);
    const body = el('div', 'bs-ord__step-body');
    body.appendChild(el('div', 'bs-ord__step-label', s.label));
    body.appendChild(el('div', 'bs-ord__step-meta', s.meta));
    li.appendChild(body);
    list.appendChild(li);
  });
  card.appendChild(list);
  return card;
}

function buildTransfer(order) {
  const t = transferOf(order);
  const card = section('bs-ord__card bs-ord__pay', 'os-pay');
  card.appendChild(h2('bs-ord__h2 bs-ord__pay-title', T.pay.title, 'os-pay'));
  const grid = el('div', 'bs-ord__pay-grid' + (t.qr ? '' : ' bs-ord__pay-grid--noqr'));
  if (t.qr) {
    const fig = el('figure', 'bs-ord__qr');
    const frame = el('span', 'bs-ord__qr-box');
    const img = el('img', 'bs-ord__qr-img');
    img.src = t.qr;
    img.alt = T.pay.qrAlt(t.amount);
    frame.appendChild(img);
    fig.appendChild(frame);
    fig.appendChild(el('figcaption', 'bs-ord__qr-cap', T.pay.qrCaption));
    grid.appendChild(fig);
  }
  const rows = el('div', 'bs-ord__pay-rows');
  [
    [T.pay.amount, t.amount, t.amountCopy, T.pay.copyWhat.amount],
    [T.pay.account, t.account, t.account, T.pay.copyWhat.account],
    [T.pay.vs, t.vs, t.vs, T.pay.copyWhat.vs],
  ].forEach(function (r) {
    const row = el('div', 'bs-ord__pay-row');
    const text = el('div', 'bs-ord__pay-cell');
    text.appendChild(el('div', 'bs-ord__pay-label', r[0]));
    text.appendChild(el('div', 'bs-ord__pay-value', r[1]));
    row.appendChild(text);
    row.appendChild(copyButton('bs-ord__copy', r[2], r[3]));
    rows.appendChild(row);
  });
  if (t.iban) rows.appendChild(el('div', 'bs-ord__pay-bank', T.pay.bankLine(t.iban, t.bic)));
  grid.appendChild(rows);
  card.appendChild(grid);
  const note = el('div', 'bs-ord__note');
  note.appendChild(icon('info'));
  note.appendChild(el('span', null, T.pay.note));
  card.appendChild(note);
  return card;
}

function buildCardPay(order) {
  const card = section('bs-ord__card bs-ord__paycard', 'os-card');
  card.appendChild(box('bs-ord__paycard-icon', 'card'));
  const body = el('div', 'bs-ord__paycard-body');
  body.appendChild(h2('bs-ord__h2', T.pay.cardTitle, 'os-card'));
  body.appendChild(el('p', 'bs-ord__paycard-text', T.pay.cardText));
  card.appendChild(body);
  const total = order.total || {};
  const amount = money(Number(total.to_pay) > 0 ? total.to_pay : total.with_vat, true) || '';
  const cta = arrowLink(httpsUrl(order.payment.online_link), 'bs-ord__pay-cta', T.pay.cardCta(amount));
  cta.rel = 'noopener';
  card.appendChild(cta);
  return card;
}

// Kam zásilka míří: výdejní místo z objednávky, prodejna z manifestu, jinak
// jen způsob doručení (adresu zákazníka API záměrně neposílá)
function destination(order) {
  const kind = shippingKind(order);
  const s = order.shipping || {};
  if (kind === 'pickup' && s.pickup) {
    return { label: T.ship.destPickup, icon: 'pin', name: s.pickup.name || s.method || '', addr: [s.pickup.street, s.pickup.city].filter(Boolean).join(', ') };
  }
  if (kind === 'store') return { label: T.ship.destStore, icon: 'store', name: T.ship.storeName, addr: SHOP.store ? SHOP.store.address : '' };
  return { label: kind === 'pickup' ? T.ship.destPickup : T.ship.destAddress, icon: kind === 'pickup' ? 'pin' : 'truck', name: s.method || '', addr: '' };
}

function buildDest(order) {
  const d = destination(order);
  const card = section('bs-ord__card bs-ord__dest', 'os-dest');
  card.appendChild(box('bs-ord__dest-icon', d.icon));
  const body = el('div', 'bs-ord__dest-body');
  body.appendChild(h2('bs-ord__dest-label', d.label, 'os-dest'));
  body.appendChild(el('div', 'bs-ord__dest-name', d.name));
  if (d.addr) body.appendChild(el('div', 'bs-ord__dest-addr', d.addr));
  card.appendChild(body);
  return card;
}

function carrierMark(carrier) {
  const mark = el('span', 'bs-ord__carrier');
  const id = carrier && (hasLogo(carrier.id) ? carrier.id : carrierLogoId(carrier.name));
  const svg = id ? logo(id, 'bs-ord__carrier-logo') : null;
  if (svg) {
    ensureLogos();
    mark.appendChild(svg);
    mark.appendChild(el('span', 'sr-only', carrier.name));
  } else {
    mark.appendChild(el('span', 'bs-ord__carrier-name', carrier ? carrier.name : ''));
  }
  return mark;
}

function buildParcel(p, withLine) {
  const row = el('div', 'bs-ord__parcel');
  row.appendChild(carrierMark(p.carrier));
  const body = el('div', 'bs-ord__parcel-body');
  body.appendChild(el('div', 'bs-ord__parcel-label', T.ship.trackNo));
  const num = el('div', 'bs-ord__parcel-no');
  num.appendChild(el('span', 'bs-ord__parcel-num', p.number));
  num.appendChild(copyButton('bs-ord__copy bs-ord__copy--sm', p.number, T.pay.copyWhat.tracking));
  body.appendChild(num);
  if (withLine && p.line) body.appendChild(el('div', 'bs-ord__parcel-line', p.line));
  row.appendChild(body);
  if (p.url) {
    const follow = arrowLink(p.url, 'bs-ord__follow', T.ship.follow(p.carrier ? p.carrier.name : ''));
    follow.target = '_blank';
    follow.rel = 'noopener noreferrer';
    row.appendChild(follow);
  }
  return row;
}

function tile(iconName, cls, label, value, sub, mod) {
  const t = el('div', 'bs-ord__tile' + (mod ? ' bs-ord__tile--' + mod : ''));
  t.appendChild(icon(iconName, 'bs-ord__tile-icon ' + cls));
  const body = el('div', 'bs-ord__tile-body');
  body.appendChild(el('div', 'bs-ord__tile-label', label));
  body.appendChild(el('div', 'bs-ord__tile-value', value));
  if (sub) body.appendChild(el('div', 'bs-ord__tile-sub', sub));
  t.appendChild(body);
  return t;
}

function buildTracking(order, now) {
  const card = section('bs-ord__card bs-ord__track', 'os-track');
  card.appendChild(h2('bs-ord__h2 bs-ord__track-title', T.ship.trackTitle, 'os-track'));
  trackingOf(order).forEach(function (p) {
    card.appendChild(buildParcel(p, true));
  });
  const tiles = el('div', 'bs-ord__tiles');
  const est = estimateOf(order, now);
  if (est) tiles.appendChild(tile('calendar', 'is-green', T.ship.estimate, estimateLabel(est)));
  const d = destination(order);
  tiles.appendChild(tile(d.icon, '', d.label, d.name, d.addr, 'dest'));
  card.appendChild(tiles);
  return card;
}

function buildPickup(order) {
  const d = destination(order);
  const card = section('bs-ord__card bs-ord__pickup', 'os-pickup');
  card.appendChild(h2('bs-ord__h2 bs-ord__pickup-title', T.ship.pickupTitle, 'os-pickup'));
  const place = el('div', 'bs-ord__place');
  place.appendChild(box('bs-ord__place-icon', 'pin'));
  const body = el('div', 'bs-ord__place-body');
  body.appendChild(el('div', 'bs-ord__place-name', d.name));
  if (d.addr) body.appendChild(el('div', 'bs-ord__place-addr', d.addr));
  body.appendChild(el('p', 'bs-ord__place-note', T.ship.pickupNote));
  place.appendChild(body);
  card.appendChild(place);
  trackingOf(order).forEach(function (p) {
    card.appendChild(buildParcel(p, false));
  });
  return card;
}

function storeOpenLine(now) {
  if (isOpenNow(now)) return { open: true, text: T.ship.openToday(hourText(openWindow(now.getDay()).to)) };
  const day = nextPickupDay(now);
  const win = openWindow(day.getDay());
  return { open: false, text: win ? T.ship.closedOpens(pickupWhen(now), hourText(win.from)) : '' };
}

function buildStore(now) {
  const card = section('bs-ord__card bs-ord__store', 'os-store');
  card.appendChild(h2('bs-ord__h2 bs-ord__store-title', T.ship.storeTitle, 'os-store'));
  const row = el('div', 'bs-ord__store-row');
  row.appendChild(box('bs-ord__place-icon', 'store'));
  const body = el('div', 'bs-ord__store-body');
  body.appendChild(el('div', 'bs-ord__place-name', T.ship.storeName));
  if (SHOP.store) body.appendChild(el('div', 'bs-ord__place-addr', SHOP.store.address));
  const open = storeOpenLine(now);
  if (open.text) {
    const line = el('div', 'bs-ord__store-open' + (open.open ? ' is-open' : ''));
    line.appendChild(icon('clock'));
    line.appendChild(el('span', null, open.text));
    body.appendChild(line);
  }
  if (CONTACT.storeHours) body.appendChild(el('div', 'bs-ord__store-hours', CONTACT.storeHours));
  row.appendChild(body);
  if (SHOP.store && SHOP.store.mapUrl) {
    const map = link(SHOP.store.mapUrl, 'bs-ord__map');
    map.target = '_blank';
    map.rel = 'noopener noreferrer';
    map.appendChild(icon('map'));
    map.appendChild(el('span', null, T.ship.map));
    row.appendChild(map);
  }
  card.appendChild(row);
  return card;
}

function buildReturns(order) {
  const card = section('bs-ord__card bs-ord__returns', 'os-returns');
  card.appendChild(h2('bs-ord__h2 bs-ord__returns-title', T.returns.title, 'os-returns'));
  const tiles = el('div', 'bs-ord__tiles');
  const rtile = function (href, iconName, title, text, cta) {
    const a = link(href, 'bs-ord__rtile');
    a.appendChild(box('bs-ord__rtile-icon', iconName));
    a.appendChild(el('span', 'bs-ord__rtile-title', title));
    a.appendChild(el('span', 'bs-ord__rtile-text', text));
    const more = el('span', 'bs-ord__rtile-cta');
    more.appendChild(el('span', null, cta));
    more.appendChild(icon('arrow-right'));
    a.appendChild(more);
    return a;
  };
  const withdrawal = page('withdrawal');
  const claim = page('claim');
  if (withdrawal) tiles.appendChild(rtile(withdrawal, 'refresh', T.returns.returnTitle, T.returns.returnText(returnUntil(order)), T.returns.returnCta));
  if (claim) tiles.appendChild(rtile(claim, 'shield', T.returns.claimTitle, T.returns.claimText, T.returns.claimCta));
  card.appendChild(tiles);
  return card;
}

function buildCancelled() {
  const card = section('bs-ord__card bs-ord__cancel', 'os-cancel');
  card.appendChild(h2('bs-ord__h2', T.cancelled.title, 'os-cancel'));
  card.appendChild(el('p', 'bs-ord__cancel-text', T.cancelled.text(chatOn())));
  card.appendChild(arrowLink('/', 'bs-ord__more', T.cancelled.cta));
  return card;
}

// Samoobslužné storno (přepínač orderCancel, `cancel.allowed` z API): tlačítko a potvrzení
// v téže kartě. Rozhoduje API nad čerstvým detailem; po zrušení i po odmítnutí (objednávka
// mezitím šla dál) stránka načte nový stav, cache API se při stornu maže. Výpadek: storno
// mohlo projít, proto taky nové načtení místo druhého pokusu.
function buildAbort(app, order) {
  const card = section('bs-ord__card bs-ord__abort', 'os-abort');
  const title = h2('bs-ord__h2', T.abort.title, 'os-abort');
  const text = el('p', 'bs-ord__abort-text', T.abort.text);
  const actions = el('div', 'bs-ord__abort-actions');
  card.appendChild(title);
  card.appendChild(text);
  card.appendChild(actions);

  function button(cls, label, iconName) {
    const b = el('button', cls);
    b.type = 'button';
    if (iconName) b.appendChild(icon(iconName));
    b.appendChild(el('span', null, label));
    return b;
  }
  function focus(node) {
    try {
      node.focus({ preventScroll: true });
    } catch (e) {
      /* starý prohlížeč bez options */
    }
  }

  function offer() {
    card.classList.remove('is-confirm');
    title.textContent = T.abort.title;
    text.textContent = T.abort.text;
    actions.textContent = '';
    const start = button('bs-ord__abort-btn', T.abort.cta, 'close');
    start.addEventListener('click', ask);
    actions.appendChild(start);
    return start;
  }

  function ask() {
    card.classList.add('is-confirm');
    title.textContent = T.abort.confirm(order.code);
    text.textContent = T.abort.confirmText;
    actions.textContent = '';
    const yes = button('bs-ord__abort-yes', T.abort.yes);
    const no = button('bs-ord__abort-no', T.abort.no);
    no.addEventListener('click', function () {
      focus(offer());
    });
    yes.addEventListener('click', function () {
      if (yes.disabled || !app.config) return;
      yes.disabled = true;
      no.disabled = true;
      yes.lastChild.textContent = T.abort.working;
      cancelOrder(app.config, app.code, app.email).then(function (res) {
        if (res.ok) {
          showFeedback(T.abort.done, 'success');
          app.lookup(app.code, app.email, 'refresh');
          return;
        }
        if (res.error === 'not_cancellable' || res.error === 'not_found' || res.error === 'outage') {
          showFeedback(res.error === 'outage' ? T.abort.failed : T.abort.tooLate(chatOn()), 'error');
          app.lookup(app.code, app.email, 'refresh');
          return;
        }
        yes.disabled = false;
        no.disabled = false;
        yes.lastChild.textContent = T.abort.yes;
        showFeedback(res.error === 'locked' ? T.abort.locked : T.abort.failed, 'error');
      });
    });
    actions.appendChild(yes);
    actions.appendChild(no);
    focus(yes);
  }

  offer();
  return card;
}

// Odkaz na soubor návodu: otevře PDF v nové kartě jako nativní „Související soubory"
// na detailu (`target=_blank`); jméno pro čtečku nese název produktu (řádků je víc).
function manualLink(cls, iconName, url, name, file) {
  const a = link(url, cls);
  a.target = '_blank';
  a.rel = 'noopener';
  a.setAttribute('aria-label', T.manuals.aria(name, file));
  a.appendChild(icon(iconName));
  a.appendChild(el('span', null, T.manuals.cta));
  return a;
}

// Karta návodů hned pod kartou stavu (plátno návodů 9. 10. 2026, varianta A)
// Odkaz „Stáhnout návody" z e-mailu u objednávky bez návodu: místo prázdna věta
function buildManualsNone() {
  const note = el('div', 'bs-ord__note bs-ord__manuals-none');
  note.setAttribute('tabindex', '-1');
  note.appendChild(icon('info'));
  note.appendChild(el('span', null, T.manuals.none));
  return note;
}

function buildManuals(list) {
  const card = section('bs-ord__card bs-ord__manuals', 'os-manuals');
  const head = el('div', 'bs-ord__manuals-head');
  head.appendChild(box('bs-ord__manuals-icon', 'book'));
  const titles = el('div', 'bs-ord__manuals-titles');
  titles.appendChild(h2('bs-ord__h2', T.manuals.title(list.length), 'os-manuals'));
  const pdf = list.every(function (m) {
    return m.kind === 'PDF';
  });
  if (pdf) titles.appendChild(el('div', 'bs-ord__manuals-sub', T.manuals.sub));
  head.appendChild(titles);
  card.appendChild(head);
  const ul = el('ul', 'bs-ord__manuals-list');
  list.forEach(function (m) {
    const li = el('li', 'bs-ord__manual');
    const thumb = el('span', 'bs-ord__manual-thumb');
    if (m.image) {
      const img = el('img', 'bs-ord__manual-img');
      img.src = m.image;
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }
    li.appendChild(thumb);
    const body = el('div', 'bs-ord__manual-body');
    body.appendChild(el('div', 'bs-ord__manual-name', m.name));
    body.appendChild(el('div', 'bs-ord__manual-meta', T.manuals.meta(m.file)));
    li.appendChild(body);
    li.appendChild(manualLink('bs-ord__manual-cta', 'download', m.url, m.name, m.file));
    ul.appendChild(li);
  });
  card.appendChild(ul);
  return card;
}

function buildItems(app, order) {
  const v = itemsOf(order);
  const card = section('bs-ord__card bs-ord__items', 'os-items');
  const head = el('div', 'bs-ord__items-head');
  head.appendChild(h2('bs-ord__h2', T.items.title, 'os-items'));
  head.appendChild(el('span', 'bs-ord__items-count', v.count + ' ' + plural(v.count, T.items.count)));
  card.appendChild(head);
  const list = el('ul', 'bs-ord__list');
  const search = page('search');
  v.products.forEach(function (p) {
    const li = el('li', 'bs-ord__item' + (p.gift ? ' bs-ord__item--gift' : ''));
    const thumb = el('span', 'bs-ord__thumb');
    if (p.image) {
      const img = el('img', 'bs-ord__thumb-img');
      img.src = p.image;
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }
    thumb.appendChild(el('span', 'bs-ord__qty', p.qty));
    li.appendChild(thumb);
    const body = el('div', 'bs-ord__item-body');
    body.appendChild(el('div', 'bs-ord__item-brand', p.brand));
    // API odkaz na produkt nenese; hledání podle kódu ho najde (i k objednání znovu)
    const name = search && p.code ? link(search + '?string=' + encodeURIComponent(p.code), 'bs-ord__item-name', p.name) : el('span', 'bs-ord__item-name', p.name);
    body.appendChild(name);
    body.appendChild(el('div', 'bs-ord__item-meta', p.meta));
    if (p.manual) {
      const manual = manualLink('bs-ord__item-manual', 'book', p.manual.url, p.name, p.manual.file);
      if (p.manual.file) {
        // mezera jen pro text odkazu (kopírování, čtečka bez aria), ve flexu se nevykreslí
        manual.appendChild(document.createTextNode(' '));
        manual.appendChild(el('span', 'bs-ord__item-manual-meta', p.manual.file));
      }
      body.appendChild(manual);
    }
    li.appendChild(body);
    li.appendChild(el('div', 'bs-ord__item-price', p.price));
    list.appendChild(li);
  });
  card.appendChild(list);
  // doprava, platba, služby, slevy a zaokrouhlení (orderStatusModel.itemsOf)
  if (v.extras.length) {
    card.appendChild(el('div', 'bs-ord__sep'));
    const extras = el('div', 'bs-ord__extras');
    v.extras.forEach(function (x) {
      const row = el('div', 'bs-ord__extra');
      row.appendChild(el('span', 'bs-ord__extra-name', x.name));
      row.appendChild(el('span', 'bs-ord__extra-price', x.price));
      extras.appendChild(row);
    });
    card.appendChild(extras);
  }
  // zrušená objednávka řádek Celkem nemá: Shoptet jí při stornu cenu nuluje (QA 8. 10. 2026)
  if (v.showTotal) {
    card.appendChild(el('div', 'bs-ord__sep bs-ord__sep--total'));
    const total = el('div', 'bs-ord__total');
    const label = el('span', 'bs-ord__total-label', v.totalLabel + ' ');
    label.appendChild(el('span', 'bs-ord__total-vat', T.items.vat));
    total.appendChild(label);
    total.appendChild(el('span', 'bs-ord__total-value', v.total));
    card.appendChild(total);
  }
  documentsOf(order).forEach(function (doc) {
    card.appendChild(buildDoc(app, doc));
  });
  return card;
}

function buildDoc(app, doc) {
  const row = el('div', 'bs-ord__doc');
  row.appendChild(box('bs-ord__doc-icon', 'file'));
  const body = el('div', 'bs-ord__doc-body');
  body.appendChild(el('div', 'bs-ord__doc-title', doc.label));
  body.appendChild(el('div', 'bs-ord__doc-sub', T.items.pdf));
  row.appendChild(body);
  const btn = el('button', 'bs-ord__doc-btn');
  btn.type = 'button';
  btn.appendChild(icon('download'));
  const label = el('span', null, doc.cta);
  btn.appendChild(label);
  btn.addEventListener('click', function () {
    if (btn.disabled || !app.config) return;
    btn.disabled = true;
    label.textContent = T.items.downloading;
    fetchDocument(app.config, app.code, app.email, doc).then(function (res) {
      btn.disabled = false;
      label.textContent = doc.cta;
      if (!res.ok) {
        showFeedback(res.error === 'locked' ? T.items.downloadLocked : T.items.downloadFailed, 'error');
        return;
      }
      const url = URL.createObjectURL(res.blob);
      const a = link(url);
      a.download = (doc.type === 'invoice' ? T.items.fileInvoice : T.items.fileCreditNote) + '-' + doc.code + '.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 30000);
    });
  });
  row.appendChild(btn);
  return row;
}

function buildStale(app, data) {
  const at = toDate(data.updated_at);
  const bar = el('div', 'bs-ord__stale');
  bar.setAttribute('role', 'status');
  bar.appendChild(icon('clock', 'bs-ord__stale-icon'));
  bar.appendChild(el('span', 'bs-ord__stale-text', T.stale(at ? clock(at) : '')));
  const again = el('button', 'bs-ord__stale-link', T.refresh);
  again.type = 'button';
  again.addEventListener('click', function () {
    app.lookup(app.code, app.email, 'refresh');
  });
  bar.appendChild(again);
  return bar;
}

function buildResult(app, data, now, target) {
  const order = data.order;
  const chat = chatOn();
  const wrap = el('div', 'bs-ord__result');
  const st = statusOf(order, now, chat);
  const created = toDate(order.created_at);
  const at = toDate(data.updated_at);
  const meta = data.stale ? T.staleAt(at ? clock(at) : '') : updatedLabel(at, now);
  wrap.appendChild(
    statusCard(
      'bs-ord__status--result',
      st.icon,
      T.orderNo(order.code) + (created ? ' · ' + fullDate(created) : ''),
      st.title,
      st.text,
      meta
    )
  );
  const manuals = manualsOf(order);
  if (manuals.length) wrap.appendChild(buildManuals(manuals));
  else if (target === 'navody') wrap.appendChild(buildManualsNone());
  wrap.appendChild(buildSteps(order, now));
  const action = actionOf(order);
  if (action === 'transfer') wrap.appendChild(buildTransfer(order));
  else if (action === 'card') wrap.appendChild(buildCardPay(order));
  else if (action === 'dest') wrap.appendChild(buildDest(order));
  else if (action === 'tracking') wrap.appendChild(buildTracking(order, now));
  else if (action === 'pickup') wrap.appendChild(buildPickup(order));
  else if (action === 'store') wrap.appendChild(buildStore(now));
  else if (action === 'returns') wrap.appendChild(buildReturns(order));
  else if (action === 'cancelled') wrap.appendChild(buildCancelled());
  // storno jen z čerstvé odpovědi (zastaralý stav při výpadku ho nenabízí)
  const selfCancel = !data.stale && cancelOf(order, featureOn('orderCancel'));
  if (selfCancel) wrap.appendChild(buildAbort(app, order));
  wrap.appendChild(buildItems(app, order));
  const back = link('#', 'bs-ord__another');
  back.setAttribute('role', 'button');
  back.appendChild(icon('arrow-left'));
  back.appendChild(el('span', null, T.another));
  back.addEventListener('click', function (e) {
    e.preventDefault();
    app.showForm({ code: '', email: '' });
    const input = document.getElementById('os-code');
    if (input) input.focus();
  });
  wrap.appendChild(back);
  return { node: wrap, action: action, help: helpOf(order, selfCancel), selfCancel: selfCancel };
}

// ---------------------------------------------------------------------------
// Aplikace
// ---------------------------------------------------------------------------
// Cíl posunu z odkazu e-mailu: doklady (bez dokladu karta položek, kde budou), návody
// (bez návodu věta `buildManualsNone`)
function targetNode(main, target) {
  if (target === 'doklady') return main.querySelector('.bs-ord__doc') || main.querySelector('.bs-ord__items');
  if (target === 'navody') return main.querySelector('.bs-ord__manuals, .bs-ord__manuals-none');
  return null;
}

function createApp(root, main, side) {
  const app = {
    config: getOrderStatusConfig(),
    store: storageOrNull(),
    logged: loggedEmail(),
    code: '',
    email: '',
    last: null,
    timer: null,
    seq: 0,
    // „Zapomenout" ho zvýší: odpověď hledání spuštěného dřív pak paměť nezapíše
    memoryEpoch: 0,
    target: '',
  };

  function clear() {
    clearTimeout(app.timer);
    main.textContent = '';
    side.textContent = '';
  }

  app.showForm = function (opts) {
    clear();
    setChatContact('');
    root.classList.remove('is-result');
    const built = buildForm(app, opts || {});
    main.appendChild(built.card);
    side.appendChild(buildWhere());
    side.appendChild(buildHelp('form', opts && opts.code));
    return built;
  };

  app.showLoading = function (code, source) {
    clear();
    root.classList.remove('is-result');
    main.appendChild(buildLoading(code, source));
    side.appendChild(buildWhere());
    side.appendChild(buildHelp('form', code));
  };

  app.showResult = function (data) {
    clear();
    root.classList.add('is-result');
    const now = new Date();
    if (data.stale) main.appendChild(buildStale(app, data));
    // cíl z odkazu e-mailu (`k=`) platí jen pro první výsledek
    const target = app.target;
    app.target = '';
    const r = buildResult(app, data, now, target);
    main.appendChild(r.node);
    side.appendChild(buildFaq(faqOf(r.action, r.selfCancel)));
    side.appendChild(buildHelp(r.help, data.order.code));
    const title = main.querySelector('.bs-ord__status-title');
    const goal = targetNode(main, target);
    if (goal) {
      goal.scrollIntoView({ block: 'start' });
      if (!goal.hasAttribute('tabindex')) goal.setAttribute('tabindex', '-1');
      try {
        goal.focus({ preventScroll: true });
      } catch (e) {
        /* starý prohlížeč bez options */
      }
      return;
    }
    if (root.getBoundingClientRect().top < 0) root.scrollIntoView({ block: 'start' });
    if (title) {
      try {
        title.focus({ preventScroll: true });
      } catch (e) {
        /* starý prohlížeč bez options */
      }
    }
  };

  app.lookup = function (code, email, source) {
    const seq = ++app.seq;
    const epoch = app.memoryEpoch;
    if (source !== 'link') app.target = '';
    app.code = code;
    app.email = email;
    // e-mail do chatu až po ověření (číslo + e-mail sedí), do té doby žádný
    setChatContact('');
    if (source !== 'form') app.showLoading(code, source);
    if (!app.config) {
      app.showForm({ alert: 'outage', code: code, email: email });
      return Promise.resolve();
    }
    return lookupOrder(app.config, code, email).then(function (res) {
      if (seq !== app.seq) return; // novější hledání mezitím
      if (res.ok) {
        app.last = res.data;
        if (epoch === app.memoryEpoch) writeMemory(app.store, code, email);
        setChatContact(email, T.help.chatDraft(res.data.order.code, email));
        app.showResult(res.data);
        return;
      }
      // výpadek při obnově: zůstává poslední známý stav jako zastaralý, bez výzvy k platbě
      if (source === 'refresh' && res.error === 'outage' && app.last) {
        setChatContact(email, T.help.chatDraft(app.last.order.code, email));
        app.showResult(staleResult(app.last));
        return;
      }
      app.showForm({ alert: res.error, retryAfter: res.retryAfter, code: code, email: email });
    });
  };

  return app;
}

function prefill() {
  const stash = window.__bsOrderPrefill;
  if (stash) return parsePrefill(stash.h, stash.s);
  // hlavička bez prefill skriptu (HTML kódy instance ještě nepřepsané): očistit adresu tady
  const found = parsePrefill(location.hash, location.search);
  if (found) {
    const fromHash = /(^#|&)[oe]=/.test(location.hash);
    try {
      history.replaceState(history.state, '', location.pathname + cleanSearch(location.search) + (fromHash ? '' : location.hash));
    } catch (e) {
      /* bez History API zůstane adresa */
    }
  }
  return found;
}

// ---------------------------------------------------------------------------
export function initOrderStatusPage() {
  if (!pageIs('orderStatus')) return;
  const host = pageHost();
  if (!host || document.querySelector('.bs-ord')) return;

  const root = el('div', 'bs-ip bs-ord');
  root.appendChild(buildHero({ eyebrow: T.eyebrow, title: host.h1 || null, perex: T.perex }));
  const grid = el('div', 'bs-ord__grid');
  const main = el('div', 'bs-ord__main');
  const side = el('aside', 'bs-ord__side');
  grid.appendChild(main);
  grid.appendChild(side);
  root.appendChild(grid);
  host.about.classList.add('bs-ip__native');
  host.about.insertAdjacentElement('beforebegin', root);

  const app = createApp(root, main, side);
  const pre = prefill();
  if (pre && pre.code && pre.email) {
    app.target = pre.k || '';
    app.lookup(pre.code, pre.email, 'link');
  }
  else {
    const built = app.showForm({ code: pre ? pre.code : '', email: pre ? pre.email : '' });
    if (pre && pre.code && !pre.email) built.email.focus({ preventScroll: true });
  }
  markReady();
}
